import type { SummarySpec } from '@diagon-alley/common';

/**
 * The compiled spec for the Inventory Health summary, exactly as described:
 *
 *   "4 metric cards - total skus tracked, Product Groups with <15 days of stock,
 *    Healthy SKU count (>45 days of stock), Total shortfall.
 *    Then tables - Product groups with highest shortfalls; table showing % SKUs in
 *    0-7 DoH, 7-15, 15-45 and 45+; shortfall of 2 top selling categories and the
 *    top 5 selling skus within it, grouped by product group."
 *
 * "Top selling" is bound to `drr` — confirmed with the operator, and the only sales-rate
 * signal the response carries.
 */
export const INVENTORY_HEALTH_SUMMARY_SPEC: SummarySpec = {
    version: 1,
    source: 'inventoryHealth',
    blocks: [
        {
            type: 'kpis',
            columns: 4,
            items: [
                {
                    label: 'Total SKUs tracked',
                    value: { kind: 'count' },
                },
                {
                    label: 'Product groups under 15 days',
                    value: {
                        kind: 'countGroups',
                        by: 'data[].productGroup',
                        metric: {
                            kind: 'divide',
                            numerator: { kind: 'agg', agg: 'sum', path: 'data[].currentStock' },
                            denominator: { kind: 'agg', agg: 'sum', path: 'data[].drr' },
                        },
                        where: { path: '$metric', lt: 15 },
                    },
                    format: { unit: 'groups' },
                    tone: [{ when: { path: '$value', gt: 0 }, as: 'warn' }],
                },
                {
                    label: 'Healthy SKUs (45+ days cover)',
                    value: {
                        kind: 'count',
                        where: {
                            all: [
                                { path: 'data[].coverDays', gt: 45 },
                                { path: 'data[].coverDays', isNotNull: true },
                            ],
                        },
                    },
                    shareOf: { kind: 'count' },
                    helper: 'of all tracked SKUs',
                },
                {
                    label: 'Total shortfall',
                    value: { kind: 'agg', agg: 'sum', path: 'data[].shortfall' },
                    format: { unit: 'units', compact: true },
                },
            ],
        },
        {
            type: 'groupTable',
            title: 'Product groups with the highest shortfall',
            by: 'data[].productGroup',
            rankBy: { kind: 'agg', agg: 'sum', path: 'data[].shortfall' },
            direction: 'desc',
            limit: 10,
            includeOther: true,
            includeTotal: true,
            columns: [
                { label: 'SKUs', metric: { kind: 'agg', agg: 'count' } },
                {
                    label: 'Shortfall',
                    metric: { kind: 'agg', agg: 'sum', path: 'data[].shortfall' },
                    format: { unit: 'units', compact: true },
                },
                {
                    label: 'Share',
                    metric: {
                        kind: 'share',
                        of: { kind: 'agg', agg: 'sum', path: 'data[].shortfall' },
                    },
                    format: { unit: '%', decimals: 1 },
                },
                {
                    label: 'Cover (d)',
                    metric: {
                        kind: 'divide',
                        numerator: { kind: 'agg', agg: 'sum', path: 'data[].currentStock' },
                        denominator: { kind: 'agg', agg: 'sum', path: 'data[].drr' },
                    },
                    format: { decimals: 1 },
                },
            ],
        },
        {
            type: 'bandTable',
            title: 'SKUs by days of cover',
            path: 'data[].coverDays',
            edges: [7, 15, 45],
            labels: ['0–7 days', '7–15 days', '15–45 days', '45+ days'],
            nulls: 'ownBand',
            columns: [
                {
                    label: 'Shortfall',
                    metric: { kind: 'agg', agg: 'sum', path: 'data[].shortfall' },
                    format: { unit: 'units', compact: true },
                },
            ],
        },
        {
            type: 'nestedTable',
            title: 'Shortfall in the two highest-selling categories',
            by: 'data[].category',
            rankGroupsBy: { kind: 'agg', agg: 'sum', path: 'data[].drr' },
            groupLimit: 2,
            rankRowsBy: { kind: 'agg', agg: 'sum', path: 'data[].drr' },
            rowLimit: 5,
            groupColumns: [
                {
                    label: 'Daily run rate',
                    metric: { kind: 'agg', agg: 'sum', path: 'data[].drr' },
                    format: { decimals: 1 },
                },
                {
                    label: 'Shortfall',
                    metric: { kind: 'agg', agg: 'sum', path: 'data[].shortfall' },
                    format: { unit: 'units', compact: true },
                },
            ],
            rowColumns: [
                { label: 'Product group', path: 'data[].productGroup' },
                { label: 'SKU', path: 'data[].productName' },
                { label: 'DRR', path: 'data[].drr', format: { decimals: 1 } },
                { label: 'Shortfall', path: 'data[].shortfall', format: { unit: 'units' } },
                { label: 'Cover (d)', path: 'data[].coverDays', format: { decimals: 1 } },
            ],
        },
    ],
};

